import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { LayoutTemplate, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function TemplatePicker({ onApply, currentMedicines, currentDiagnosis, currentAdvice, currentFollowUp, currentNextInv }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tplName, setTplName] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/templates");
      setTemplates(r.data.templates || []);
    } catch (e) { console.debug("[silent]", e); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const apply = (t) => {
    onApply({
      medicines: (t.medicines || []).map((m) => ({ ...m })),
      diagnosis: t.diagnosis || "",
      icd10: t.icd10 || "",
      advice: t.advice || "",
      follow_up: t.follow_up || "",
      next_investigations: t.next_investigations || "",
    });
    setOpen(false);
  };

  const saveCurrent = async () => {
    if (!tplName) return;
    const meds = (currentMedicines || []).filter((m) => m.brand_name);
    await api.post("/templates", {
      name: tplName,
      diagnosis: currentDiagnosis?.primary || "",
      icd10: currentDiagnosis?.icd10 || "",
      medicines: meds,
      advice: currentAdvice || "",
      follow_up: currentFollowUp || "",
      next_investigations: currentNextInv || "",
    });
    setSaveOpen(false);
    setTplName("");
    toast.success("Template saved");
    load();
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-8 gap-1 border-slate-300"
        data-testid="open-templates-btn"
      >
        <LayoutTemplate size={12} /> Templates
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <LayoutTemplate size={18} /> Prescription templates
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {templates.map((t) => (
              <div
                key={t.template_id}
                className="border border-slate-200 rounded-md p-3 hover:border-pink-400 hover:bg-pink-50/30 cursor-pointer"
                onClick={() => apply(t)}
                data-testid={`template-card-${t.template_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.diagnosis} {t.icd10 && `· ${t.icd10}`}</div>
                  </div>
                  <div className="text-xs text-slate-500 tabular">{(t.medicines || []).length} med(s)</div>
                </div>
                {t.medicines?.length > 0 && (
                  <div className="text-[11px] text-slate-600 mt-2 flex flex-wrap gap-1">
                    {t.medicines.slice(0, 4).map((m, i) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-100 rounded">{m.brand_name} {m.strength}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {templates.length === 0 && <div className="text-sm text-slate-500 p-4 text-center">No templates yet.</div>}
          </div>
          <div className="alert-info text-xs px-3 py-2 rounded">
            Applying a template populates medicines and advice as a draft. Verify every value before saving.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setSaveOpen(true); }} data-testid="save-as-template-btn">
              <Sparkles size={12} className="mr-1" /> Save current as template
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Save as template</DialogTitle>
          </DialogHeader>
          <div>
            <label className="overline">TEMPLATE NAME</label>
            <input
              value={tplName}
              onChange={(e) => setTplName(e.target.value)}
              placeholder="e.g. T2DM starter"
              className="w-full mt-1 h-9 border border-slate-200 rounded px-3 text-sm"
              data-testid="template-name-input"
            />
            <div className="text-xs text-slate-500 mt-2">
              Will save the current diagnosis, {(currentMedicines || []).filter((m) => m.brand_name).length} medicine(s), advice and follow-up.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={saveCurrent} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="confirm-save-template">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
